import { BiImport } from 'react-icons/bi';
import { Button, ButtonProps } from '@heroui/button';
import { useTranslation } from 'react-i18next';
import { FaFileZipper } from 'react-icons/fa6';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from '@heroui/modal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert } from '@heroui/alert';
import { Select, SelectItem } from '@heroui/select';

import { ImportData, addImportToDb, readImportZip, moveSongsToSongbook } from '@/core/export.ts';
import { useInvalidateAll, useSongbooks } from '@/hooks/useSongbook.ts';
import FileDropzone from '@/components/FileDropzone/FileDropzone.tsx';
import { songName } from '@/core/song.ts';
import Spinner from '@/components/Spinner/Spinner.tsx';

interface ImportButtonProps {
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  songbookId?: string;
}

function ImportButton({ color, variant = 'flat', songbookId }: ImportButtonProps) {
  const { t } = useTranslation();
  const [currentFile, setCurrentFile] = useState<File>();
  const invalidateAll = useInvalidateAll();
  const { data: songbooksData } = useSongbooks();
  const songbookSelectRef = useRef<HTMLSelectElement>(null);

  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure({
    onClose() {
      reset();
      setCurrentFile(undefined);
    }
  });

  const { mutate, isPending, reset } = useMutation({
    mutationKey: ['importDatabase'],
    mutationFn: async (importData: ImportData) => {
      await addImportToDb(importData);
      await invalidateAll();
      onClose();
    },
  });

  const { isLoading, data, error } = useQuery({
    queryKey: ['readImportZip', currentFile],
    queryFn: () => currentFile ? readImportZip(currentFile) : Promise.resolve(undefined),
    enabled: !!currentFile,
  });

  const onDrop = useCallback(async (files: File[]) => {
    setCurrentFile(files[0]);
  }, []);

  const orderedSongs = useMemo(() => {
    if (data) {
      return [...data.songs].sort((a, b) => a.order - b.order);
    }

    return [];
  }, [data]);

  const songbookOptions = useMemo(() => {
    if (!songbooksData || !data) {
      return [];
    }

    return [
      { value: data.songbooks[0].title, key: data.songbooks[0].id },
      ...songbooksData.filter((s) => s.id !== data.songbooks[0].id).map((s) => ({ value: s.title, key: s.id })),
    ];
  }, [data, songbooksData]);

  const onImport = useCallback(() => {
    if (!data) return;

    if (songbookSelectRef.current) {
      const importToId = songbookSelectRef.current.value;

      if (importToId !== data.songbooks[0].id) {
        return mutate(moveSongsToSongbook(data, importToId));
      }
    }

    return mutate(data);
  }, [data, mutate]);

  return (
    <>
      <Button
        aria-label="Open songbooks"
        color={color}
        startContent={<BiImport />}
        variant={variant}
        onPress={onOpen}
      >
        {t('songbook.home.import')}
      </Button>
      <Modal backdrop="blur" isDismissable={!isPending} isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent className="bg-default-50 text-foreground">
          <div className="w-full">
            <ModalHeader className="flex flex-col gap-1">{t('songbook.import.title')}</ModalHeader>
            {data ? (
              <>
                <ModalBody className="relative">
                  {isPending && <div className="flex absolute top-0 left-0 w-full h-full items-center justify-center bg-default-50/90 z-10"><Spinner /></div>}
                  {data.songbooks.length > 1 ? (
                    <>
                      <div className="mb-1">{t('songbook.import.songbooksTitle')}</div>
                      <div className="border-1 border-default-300 rounded-xl px-4 py-2">
                        {data.songbooks.map((songbook) => (
                          <div key={songbook.id} className="my-2 truncate">
                            {songbook.title}
                          </div>
                        ))}
                      </div>
                      <Alert color="default">
                        {t('songbook.import.songbooksInfo')}
                      </Alert>
                    </>
                  ) : (
                    <>
                      <div className="mb-1">{orderedSongs.length === 1 ? t('songbook.import.importSong') : t('songbook.import.importSongs')}</div>
                      <Select
                        ref={songbookSelectRef}
                        disallowEmptySelection
                        isRequired
                        aria-label={orderedSongs.length === 1 ? t('songbook.import.importSong') : t('songbook.import.importSongs')}
                        defaultSelectedKeys={songbookId ? [songbookId] : [songbookOptions[0].key]}
                      >
                        {songbookOptions.map((songbook) => (
                          <SelectItem key={songbook.key} className="text-foreground">
                            {songbook.value}
                          </SelectItem>
                        ))}
                      </Select>
                      <div className="mt-4 border-1 border-default-300 rounded-xl px-4 py-2">
                        {orderedSongs.map((song) => (
                          <div key={song.id} className="my-2 truncate">
                            {songName(song)}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button color="default" disabled={isPending} variant="light" onPress={() => setCurrentFile(undefined)}>
                    {t('songbook.import.goBack')}
                  </Button>
                  <Button color="primary" disabled={isPending} startContent={<BiImport />} type="submit" onPress={onImport}>
                    {t('songbook.import.import')}
                  </Button>
                </ModalFooter>
              </>
            ) : (
              <ModalBody className="pb-6">
                {error && (
                  <Alert
                    color="danger"
                    description={t('errors.invalidBackup')}
                    title={t('errors.error')}
                    variant="faded"
                  />
                )}
                {!data && (
                  <FileDropzone
                    Icon={FaFileZipper}
                    accept="application/zip,.zip"
                    isBusy={isLoading}
                    text={{
                      title: t('media.file.dragTitle', { title: t('media.file.title.backup') }),
                      supports: t('media.file.supports.backup'),
                    }}
                    onDrop={onDrop}
                  />
                )}
              </ModalBody>
            )}
          </div>
        </ModalContent>
      </Modal>
    </>
  );
}

export default ImportButton;
